"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { Button } from "@/shared/ui/buttons/button";
import { BOARD_DIALOG_CLASS, BOARD_DIALOG_HEADER, DialogBody } from "./dialog-body";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/shared/ui/modals/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/inputs/select";
import { ToggleGroup, ToggleGroupItem } from "@/shared/ui/buttons/toggle-group";
import { useCreateLfgLftPost, useUpdateLfgLftPost } from "@/hooks/useLfgLft";
import type { LfgLftPostResponse } from "@/shared/api/lfg-lft-types";
import { cn } from "@/shared/lib/utils";
import {
  POST_TYPES,
  emptyPostForm,
  languagesFromProfile,
  postToForm,
  toCreateBody,
  toUpdateBody,
  validatePostForm,
  type PostForm,
  type PostFormError,
  type PostType,
} from "../model/post-form";
import { PostFormFields } from "./post-form-fields";

export interface PostFormTeam {
  id: string;
  name: string;
}

export type PostFormMode = { kind: "create"; defaultType: PostType } | { kind: "edit"; post: LfgLftPostResponse };

interface PostFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: PostFormMode;
  /** Команды, где пользователь капитан или менеджер — авторы «Ищем игроков». */
  teams: PostFormTeam[];
  /** Смещение пояса зрителя; null — пояс неизвестен, форму не открываем. */
  tzOffset: number | null;
  tzLabel: string;
  /** Языки из профиля: у «Ищу команду» предзаполняют выбор. */
  profileLanguages: readonly string[] | undefined;
}

const SEGMENT_ON = "data-[state=on]:bg-highlight-soft data-[state=on]:text-highlight";

function initialForm(p: PostFormDialogProps, tz: number): PostForm {
  if (p.mode.kind === "edit") return postToForm(p.mode.post, tz);
  const form = emptyPostForm(p.mode.defaultType);
  return {
    ...form,
    teamId: p.teams.length === 1 ? p.teams[0]!.id : "",
    languages: p.mode.defaultType === "Lfg" ? languagesFromProfile(p.profileLanguages) : [],
  };
}

/** Тело живёт только пока диалог открыт: каждое открытие — свежее состояние формы. */
function PostFormBody(p: PostFormDialogProps & { tz: number }) {
  const t = useTranslations("LfgBoard");
  const isEdit = p.mode.kind === "edit";
  const [form, setForm] = useState(() => initialForm(p, p.tz));
  const [error, setError] = useState<PostFormError | null>(null);
  const create = useCreateLfgLftPost();
  const update = useUpdateLfgLftPost();
  const pending = create.isPending || update.isPending;

  const switchType = (type: PostType) => {
    if (type === form.type) return;
    // Роли у типов значат разное (одна основная + вторая / до четырёх искомых): не переносим.
    setForm({
      ...form,
      type,
      roles: [],
      secondary: null,
      rank: null,
      teamId: p.teams.length === 1 ? p.teams[0]!.id : "",
      languages: type === "Lfg" && form.languages.length === 0 ? languagesFromProfile(p.profileLanguages) : form.languages,
    });
    setError(null);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const err = validatePostForm(form);
    setError(err);
    if (err) return;
    const done = { onSuccess: () => p.onOpenChange(false) };
    if (p.mode.kind === "edit") update.mutate({ id: p.mode.post.id, body: toUpdateBody(form, p.tz) }, done);
    else create.mutate(toCreateBody(form, p.tz), done);
  };

  return (
    <form onSubmit={submit} className="flex flex-col">
      <DialogBody>
      {!isEdit && (
        <ToggleGroup
          type="single"
          value={form.type}
          onValueChange={(v) => {
            const next = POST_TYPES.find((x) => x === v);
            if (next) switchType(next);
          }}
          aria-label={t("form.typeLabel")}
          className="w-full gap-1.5 rounded-xl border border-border bg-muted/30 p-1.5"
        >
          {POST_TYPES.map((x) => (
            <ToggleGroupItem key={x} value={x} className={cn("h-[34px] flex-1 rounded-lg! text-[13px] font-bold text-muted-foreground", SEGMENT_ON)}>
              {x === "Lfg" ? t("segment.players") : t("segment.teams")}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      )}

      {form.type === "Lft" && !isEdit && (
        <div className="space-y-1.5">
          <h3 className="text-[11px] font-bold uppercase tracking-[1.2px] text-muted-foreground">{t("form.team")}</h3>
          {p.teams.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t("form.noTeam")}{" "}
              <Link href="/roster/create" className="font-semibold text-highlight underline-offset-2 hover:underline">
                {t("ctaButton")}
              </Link>
            </p>
          ) : (
            <Select value={form.teamId || undefined} onValueChange={(v) => setForm({ ...form, teamId: v })}>
              <SelectTrigger aria-label={t("form.team")}>
                <SelectValue placeholder={t("form.teamPlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {p.teams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>{team.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      )}

      {form.type === "Lfg" && !isEdit && <p className="text-xs text-muted-foreground">{t("form.replacesPrevious")}</p>}

      <PostFormFields form={form} onChange={(next) => { setForm(next); setError(null); }} tzLabel={p.tzLabel} />

      {error && <p role="alert" className="text-sm text-destructive">{t(`form.error.${error}`)}</p>}
      </DialogBody>

      <DialogFooter className="border-t border-border px-6 py-4">
        <Button type="button" variant="outline" onClick={() => p.onOpenChange(false)}>{t("card.cancel")}</Button>
        <Button
          type="submit"
          disabled={pending || (form.type === "Lft" && !isEdit && p.teams.length === 0)}
          className="border border-highlight-border bg-highlight-soft font-bold text-highlight hover:bg-highlight-soft/80"
        >
          {isEdit ? t("form.save") : t("form.publish")}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function PostFormDialog(p: PostFormDialogProps) {
  const t = useTranslations("LfgBoard");
  return (
    <Dialog open={p.open && p.tzOffset !== null} onOpenChange={p.onOpenChange}>
      <DialogContent className={BOARD_DIALOG_CLASS}>
        <DialogHeader className={BOARD_DIALOG_HEADER}>
          <DialogTitle className="font-heading text-[22px]">{p.mode.kind === "edit" ? t("form.titleEdit") : t("form.titleNew")}</DialogTitle>
          <DialogDescription className="sr-only">{t("form.description")}</DialogDescription>
        </DialogHeader>
        {p.tzOffset !== null && <PostFormBody {...p} tz={p.tzOffset} />}
      </DialogContent>
    </Dialog>
  );
}
